import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, kebabCase, get, truncate } from 'lodash'
import { IconCheck, IconX, IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import * as Yup from 'yup'

import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { setTemporaryCriteria, editScorecardConfig } from '@/reducers/qa-copilot/qa-copilot.redux'
import { EditButton } from '@/components/buttons/EditButton'
import { openModal } from '@/reducers/ui/ui.redux'
import { setTemporaryPrompt } from '@/reducers/prompts/prompts.redux'
import { addTemporaryPromptHelper } from '@/reducers/prompts/prompts.helpers'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import { Button } from '@/components/buttons/Button'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'

import { QACopilotSectionSettingsModal } from '../modals/QACopilotSectionSettingsModal'
import { QACopilotDeleteSectionModal } from '../modals/QACopilotDeleteSectionModal'
import { addTemporaryCriteriaHelper, updateSectionNameHelper } from '../QACopilot.helpers'

export const QACopilotScorecardSection = ({
  section,
  collapsed,
  setCollapsed,
  isOutdatedScorecard,
}) => {
  const dispatch = useDispatch()
  const [editingSectionName, setEditingSectionName] = useState(false)
  const { temporaryCriteria, scorecardConfig } = useSelector((state) => state.qaCopilot)
  const deleteModalId = `qaCopilot/deleteSection-${section.ssid}`
  const sectionSettingsModalId = `qaCopilot/sectionSettings-${section.ssid}`
  const allSectionsAreEmpty = !scorecardConfig.sections.some(
    (section) => !isEmpty(get(section, 'measures[0].criteria'))
  )
  const showActions = !editingSectionName && !temporaryCriteria && !allSectionsAreEmpty

  const handleOpenDeleteModal = () => {
    dispatch(openModal(deleteModalId))
  }

  const handleOpenSectionSettingsModal = () => {
    dispatch(openModal(sectionSettingsModalId))
  }

  const handleAddCriteria = () => {
    const tempCriteria = addTemporaryCriteriaHelper(section)
    dispatch(setTemporaryCriteria(tempCriteria))
    dispatch(setTemporaryPrompt(addTemporaryPromptHelper(tempCriteria.scid)))
  }

  const handleUpdateSectionName = (values) => {
    dispatch(
      editScorecardConfig(updateSectionNameHelper(scorecardConfig, { ...section, ...values }))
    )
    setEditingSectionName(false)
  }

  return (
    <>
      <section className="qa-copilot-scorecard-section" id={kebabCase(section.name)}>
        <div className="small-gap vertical-center">
          <Button square basic small onClick={setCollapsed}>
            {collapsed ? <IconChevronRight /> : <IconChevronDown />}
          </Button>
          <div className="small-gap vertical-center">
            {!editingSectionName ? (
              <>
                <InteractiveTooltip
                  interactive
                  hide={section.name?.length < 60}
                  content={section.name}
                  placement="bottom-start"
                >
                  <h2>{truncate(section.name, { length: 60 })}</h2>
                </InteractiveTooltip>
                <EditButton
                  onEdit={() => setEditingSectionName(!editingSectionName)}
                  disabled={isOutdatedScorecard}
                />
              </>
            ) : (
              <AbstractForm
                horizontal
                noLabel
                onSubmit={handleUpdateSectionName}
                existingValues={{ name: section.name }}
                schema={[
                  {
                    name: 'name',
                    type: 'text',
                    required: true,
                    autoFocus: true,
                    autoComplete: 'off',
                    customValidators: [
                      Yup.string()
                        .required('QA Scorecard section name is required')
                        .max(500, 'Must be less than five hundred characters'),
                    ],
                  },
                ]}
                buttonProps={{ square: true }}
                closeButtonProps={{ square: true }}
                buttonLabel={<IconCheck />}
                closeButtonLabel={<IconX />}
                onClose={() => setEditingSectionName(false)}
              />
            )}
          </div>
        </div>
        {showActions && (
          <div className="flex vertical-center gap">
            <Button
              basic
              small
              onClick={handleAddCriteria}
              disabled={!!temporaryCriteria || isOutdatedScorecard}
            >
              <span>Add Criterion</span>
            </Button>
            <Button
              basic
              small
              onClick={handleOpenSectionSettingsModal}
              data-testid={kebabCase(`section-settings-${section.name}`)}
            >
              <span>Section Settings</span>
            </Button>
            <ActionsDropdown
              actions={[
                {
                  label: 'Delete Section',
                  onClick: handleOpenDeleteModal,
                  isDanger: true,
                  disabled: isOutdatedScorecard,
                },
              ]}
              buttonProps={{ basic: true }}
            />
          </div>
        )}
      </section>

      <QACopilotDeleteSectionModal section={section} />
      <QACopilotSectionSettingsModal section={section} />
    </>
  )
}
